import {createContext, useContext, useMemo, useState, type ReactNode} from 'react'

export type Language = 'ru' | 'en'

interface Translations {
  header: {
    contact: string
  }
  hero: {
    titleBefore: string
    titleHighlight: string
    titleAfter: string
    subtitle: string
    ctaPrimary: string
    ctaSecondary: string
    imageAlt: string
  }
  maxGames: {
    title: string
    subtitle: string
    games: { calories: string; match3: string; quizMax: string }
  }
  stats: {
    title: string
    items: { value: string; label: string; highlight?: boolean }[]
    bottomBefore: string
    bottomHighlight: string
    bottomAfter: string
  }
  features: {
    title: string
    items: { title: string; description: string }[]
  }
  cta: {
    primaryTitle: string
    primarySubtitle: string
    primaryButton: string
    secondaryButton: string
    ownerTitle: string
    ownerSubtitle: string
    ownerButton: string
  }
  footer: {
    tagline: string
  }
}

const translations: Record<Language, Translations> = {
  ru: {
    header: {
      contact: 'Связаться с нами',
    },
    hero: {
      titleBefore: 'Получайте клиентов через размещение ',
      titleHighlight: 'видео рекламы',
      titleAfter: ' в Телеграм и Максе',
      subtitle: 'Платформа видео рекламы в TMA и ботах в Telegram и Max с охватом более 200 млн человек',
      ctaPrimary: 'Получить клиентов',
      ctaSecondary: 'Скачать презентацию',
      imageAlt: 'Платформа видеорекламы NYGMA',
    },
    maxGames: {
      title: 'Теперь и в Максе!',
      subtitle:
        'NYGMA запустилась в Max со своими приложениями для размещения рекламы. Попробуйте наши игры прямо сейчас:',
      games: { calories: 'Калории', match3: 'Match 3', quizMax: 'QuizMax' },
    },
    stats: {
      title: 'Растущий рынок Telegram Mini Apps',
      items: [
        { value: '5000+', label: 'Приложений в экосистеме', highlight: true },
        { value: '200M+', label: 'Пользователей' },
        { value: '131%', label: 'Месячный рост' },
        { value: '25+', label: 'Категорий приложений' },
      ],
      bottomBefore: 'Приложения в Телеграм (TMA) являются одним из самых ',
      bottomHighlight: 'быстрорастущих новых маркетинговых каналов',
      bottomAfter: ' с совокупным охватом более 200 млн человек',
    },
    features: {
      title: 'Почему выбирают NYGMA',
      items: [
        {
          title: 'Точное таргетирование',
          description:
            'Размещение видео рекламы в сотнях приложений позволит поймать целевую аудиторию в незаклаттеренном конкурентами канале',
        },
        {
          title: 'Быстрый рост',
          description: 'Воспользуйтесь преимуществами самого быстрорастущего рекламного канала с месячным ростом 131%',
        },
        {
          title: 'Огромный охват',
          description: 'Доступ к аудитории более 200 миллионов активных пользователей Telegram приложений по всему миру',
        },
      ],
    },
    cta: {
      primaryTitle: 'Готовы начать привлекать клиентов?',
      primarySubtitle: 'Присоединяйтесь к революции Telegram рекламы уже сегодня',
      primaryButton: 'Запустить кампанию',
      secondaryButton: 'Узнать больше',
      ownerTitle: 'Владелец TMA?',
      ownerSubtitle: 'Если вы владелец ТМА — мы можем вас быстро монетизировать, напишите сюда',
      ownerButton: 'Написать нам',
    },
    footer: {
      tagline: 'Платформа видео рекламы для TMA и ботов в Telegram и Max',
    },
  },
  en: {
    header: {
      contact: 'Contact us',
    },
    hero: {
      titleBefore: 'Get customers through ',
      titleHighlight: 'video advertising',
      titleAfter: ' in Telegram and Max',
      subtitle: 'Video advertising platform in TMAs and bots in Telegram and Max reaching over 200M people',
      ctaPrimary: 'Get customers',
      ctaSecondary: 'Download presentation',
      imageAlt: 'NYGMA video advertising platform',
    },
    maxGames: {
      title: 'Now in Max too!',
      subtitle:
        "NYGMA has launched in Max with its own apps for ad placement. Try our games right now:",
      games: { calories: 'Calories', match3: 'Match 3', quizMax: 'QuizMax' },
    },
    stats: {
      title: 'Growing Telegram Mini Apps market',
      items: [
        { value: '5000+', label: 'Apps in the ecosystem', highlight: true },
        { value: '200M+', label: 'Users' },
        { value: '131%', label: 'Monthly growth' },
        { value: '25+', label: 'App categories' },
      ],
      bottomBefore: 'Telegram apps (TMA) are one of the ',
      bottomHighlight: 'fastest-growing new marketing channels',
      bottomAfter: ' with a combined reach of over 200M people',
    },
    features: {
      title: 'Why choose NYGMA',
      items: [
        {
          title: 'Precise targeting',
          description:
            'Placing video ads across hundreds of apps to reach your target audience in an uncluttered channel free from competitors',
        },
        {
          title: 'Rapid growth',
          description: 'Take advantage of the fastest-growing ad channel with 131% monthly growth',
        },
        {
          title: 'Massive reach',
          description: 'Access an audience of over 200 million active Telegram and Max app users worldwide',
        },
      ],
    },
    cta: {
      primaryTitle: 'Ready to start attracting customers?',
      primarySubtitle: 'Join the Telegram advertising revolution today',
      primaryButton: 'Launch a campaign',
      secondaryButton: 'Learn more',
      ownerTitle: 'TMA owner?',
      ownerSubtitle: "If you own a TMA - we can quicly monetize it for you, reach out here",
      ownerButton: 'Contact us',
    },
    footer: {
      tagline: 'Video advertising platform for TMAs and bots in Telegram and Max',
    },
  },
}

interface LanguageContextValue {
  language: Language
  toggleLanguage: () => void
  t: Translations
}

const LanguageContext = createContext<LanguageContextValue | null>(null)

export function LanguageProvider({children}: {children: ReactNode}) {
  const [language, setLanguage] = useState<Language>('ru')

  const value = useMemo<LanguageContextValue>(
    () => ({
      language,
      toggleLanguage: () => setLanguage(prev => (prev === 'ru' ? 'en' : 'ru')),
      t: translations[language],
    }),
    [language]
  )

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export function useLanguage() {
  const ctx = useContext(LanguageContext)
  if (!ctx) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return ctx
}
